import React, { useEffect, useState } from 'react';
import { LeadTimeOptionCard, LeadTimePriceOptions, ErrorRender } from '@partsbadger/library';
import { QuoteLineItemProps as QuoteLineItemPropsFromType } from '@partsbadger/types';

import { Alert, Breadcrumb, Button, Tooltip, Typography, notification, Spin } from 'antd';

import QuoteStore from '../../../../stores/QuoteStore';
import { QuoteLineItemProps, QuoteProps } from '../../../../types/types';
import _ from 'lodash';

import { CloseOutlined, LoadingOutlined } from '@ant-design/icons';
import QuoteQuantityInput from '../../../../components/Quotes/QuoteQuantityInput';

const { Text } = Typography;

type StandardLineItemProps = {
    item: QuoteLineItemProps;
    onRemove: (item: QuoteLineItemProps) => void;
    onChangePrice: (priceId: string, lineItemId: number) => void;
    onRequote: (quoteId?: number) => void;
    setQuoteItem: (item: QuoteLineItemProps) => void;
    setShowRequestNewQuote: (v: boolean) => void;
    setQuote: (q: QuoteProps) => void;
    quoteId: number;
};

export const RenderStandardLineItem = React.memo(function RenderStandardLineItem({
    item,
    onRemove,
    onChangePrice,
    onRequote,
    setQuoteItem,
    setShowRequestNewQuote,
    setQuote,
    quoteId,
}: StandardLineItemProps) {
    const [loadingPriceOptions, setLoadingPriceOptions] = useState<boolean>(false);

    const handleChangeQuantity = async (_item: QuoteLineItemProps, qty: number) => {
        setLoadingPriceOptions(true);

        await QuoteStore.updateLineItem(quoteId, {
            quote_has_product: _item.id,
            quantity: qty,
        })
            .then(data => {
                setLoadingPriceOptions(false);

                if (data?.quote) {
                    setQuote(data.quote);
                }

                return data;
            })
            .catch(error => {
                setLoadingPriceOptions(false);

                notification.error({
                    message: 'Error',
                    description: <ErrorRender error={error} />,
                });
            });
    };

    return (
        <div className="w-full">
            <div className="flex flex-col items-end ml-auto">
                <div className="h-full flex flex-col justify-center">
                    <div className="d-flex justify-content-end" style={{ width: 40 }}>
                        <Tooltip title="Remove From Quote">
                            <Button
                                type="link"
                                className="m-0 p-0"
                                onClick={() => {
                                    QuoteStore.remove_line_items(item);
                                    onRemove(item);
                                }}
                            >
                                <CloseOutlined />
                            </Button>
                        </Tooltip>
                    </div>
                </div>

                <QuoteQuantityInput
                    quantity={item.quantity}
                    max_quantity={item.quantity_range.upper}
                    editable={item.vendor_quoted_price === '0.00'}
                    handleChangeQuantity={qty => handleChangeQuantity(item, qty)}
                />

                {item.safe_auto_quote ? (
                    <LeadTimePriceOptions
                        lineItem={(item as unknown) as QuoteLineItemPropsFromType}
                        handleChange={(priceId: string) => onChangePrice(priceId, item.id)}
                        loading={loadingPriceOptions}
                    />
                ) : (
                    <>
                        {item.needs_manual_quote && item?.is_original_item_auto_safe ? (
                            <Spin spinning={loadingPriceOptions} indicator={<LoadingOutlined />}>
                                <div className="flex flex-col items-end">
                                    <Alert
                                        showIcon={false}
                                        message="Manual Quote Required"
                                        description={
                                            'This part has features or specifications that require a manual review by one of our engineers.'
                                        }
                                    />
                                    <div className="text-red-500 mt-2">
                                        Do you need a different quantity? Please{' '}
                                        <a className="text-blue-600" onClick={() => onRequote(item.quote)}>
                                            submit for a Requote.
                                        </a>
                                    </div>
                                </div>
                            </Spin>
                        ) : (
                            <div>
                                <LeadTimeOptionCard
                                    option={{
                                        id: 'price',
                                        lead_time: item.lead_time,
                                        unit_price: parseFloat(item.unit_price),
                                        safe_auto_quote: false,
                                        total_price: parseFloat(item.total_price),
                                        quantity: item.quantity,
                                        expiration_date: '',
                                    }}
                                    readonly
                                    lineItem={(item as unknown) as QuoteLineItemPropsFromType}
                                    isDisabled={false}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>

            {item.vendor_quoted_price !== '0.00' && !item.has_dynamic_quantities && (
                <div className="text-red-500 ml-4 mt-2">
                    Do you need a different quantity? Please submit a{' '}
                    <span
                        className="text-red-700 link"
                        onClick={() => {
                            setQuoteItem(item);
                            setShowRequestNewQuote(true);
                        }}
                    >
                        Requote
                    </span>
                </div>
            )}
        </div>
    );
});
